import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import WaveUISwitch from '@/shared/components/forms/WaveUISwitch';
import TableInstance from '@/shared/constants/tableInstance';
import useAuth from '@/shared/contexts/auth/hooks/useAuth';
import User from '@/shared/types/user';

import useUsersTable_v2 from './hooks/useUsersTable_v2';

type UsersTable_v2Props = {
  refreshTableIndex: number;
  openUpdateUserModal: (user: User) => void;
  openCreateUserModal: () => void;
  onToggleUserEnabled: ({
    user,
    shouldConfirmUpdate,
  }: {
    user: User;
    shouldConfirmUpdate?: boolean;
  }) => void;
};

const UsersTable_v2: FC<UsersTable_v2Props> = ({
  refreshTableIndex,
  openUpdateUserModal,
  openCreateUserModal,
  onToggleUserEnabled,
}) => {
  const { user: currentUser } = useAuth();

  const { t } = useTranslation(['usersManagement', 'common']);

  const { apiRef, model, table, columns, visibilityModel, setVisibilityModel } =
    useUsersTable_v2({ refreshTableIndex });

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'enabled':
        return {
          ...column,
          filterable: false,
          renderCell: (params) => (
            <WaveUISwitch
              checked={params.value as boolean}
              onChange={() => {
                const user = params.row as User;
                onToggleUserEnabled({
                  user: { ...user, enabled: !user.enabled },
                  shouldConfirmUpdate:
                    user.email_address === currentUser?.email_address &&
                    user.enabled,
                });
              }}
              name='enabled'
              size='small'
              label=''
              fullWidth={false}
            />
          ),
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '80vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.email_address as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        checkboxSelection={false}
        customProps={{
          tableInstance: TableInstance.Users,
          apiRef,
          model,
          toolbar: {
            title: t('usersManagement:users'),
            toolbarActions: (
              <>
                <Button
                  variant='contained'
                  size='large'
                  onClick={openCreateUserModal}
                >
                  Create User
                </Button>
              </>
            ),
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('usersManagement:update'),
                  onClick: (apiRef, rowId) => {
                    const user = apiRef.current.getRow(rowId || '') as User;
                    openUpdateUserModal(user);
                  },
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default UsersTable_v2;
